import React, { useState, useContext, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { CartContext } from 'components/cart';
import classNames from 'lib/classNames';
import Api from '../Api';
import axios from 'axios';
import { API_GATEWAY_NEW } from 'aws-exports';
import { getDefaultSubscriptionsData } from '../Utils';

const Product = React.memo(({ product_info, add_to_unlock }) => {
    const {
        preview_product,
        Utils,
        isPreview,
        cart_settings: {
            count_compare_at,
            money_format,
            rtl,
            theme: { primary, background, secondary },
            product: { sold_out }
        },
        shop_url,
        add_to_cart,
        update_cart_item,
        productNotAddedToCart,
        setVariantPickerOptions,
        setVariantsPickerShow,
        addFBTUpsell,
        cart_currency,
        rechargeSettings,
        hasWriteDiscountsScope,
        products
    } = useContext(CartContext);
    const [product, setProduct] = useState(product_info);
    const subscriptionObject = rechargeSettings?.products?.find((p) => {
        return Object.keys(p)[0] === product_info.id.toString();
    });
    const subscription = subscriptionObject && subscriptionObject[product_info.id];
    const HAS_SUBSCRIPTION =
        subscription &&
        !!subscription?.selling_plan_groups?.length &&
        rechargeSettings?.widget_settings?.published;
    const { variants } = product_info;
    const [loading, setLoading] = useState(true);
    const [sodOut, setSodOut] = useState(false);
    const [compareAtMap, setCompareAtMap] = useState({});
    const [dataFetched, setDataFetched] = useState(false);
    const [plansData, setPlansData] = useState(null);

    const unformattedPrice = useMemo(
        () =>
            Utils.getVariantPrice({
                variant: window.mu_fbt_set_default_variant
                    ? variants.find(({ title, option1, option2, option3 }) => {
                          const selectedOptions = window.mu_fbt_set_default_variant(product);
                          const selectedValues = Object.values(selectedOptions);
                          const variantOptions = [option1, option2, option3];

                          return (
                              title === Object.values(selectedOptions).join(' / ') ||
                              selectedValues.every(
                                  (value, index) => value === variantOptions[index]
                              )
                          );
                      })
                    : product.variants[0],
                cart_currency,
                isPreview,
                plansData
            }),
        [variants, cart_currency, isPreview, plansData]
    );

    const price = Utils.formatMoney(unformattedPrice * 100, money_format);
    const [original, setOriginal] = useState(0);

    const addToCartInit = useCallback(async () => {
        if (product.isExample || !product.fetched || loading) return;
        try {
            setLoading(true);
            const { variants } = product;
            let skip_picker = false;
            if (window.mu_custom_fbt_logic) {
                skip_picker = await window.mu_custom_fbt_logic(product);
            }
            if ((Utils.productHasOneVariant({ variants }) && !HAS_SUBSCRIPTION) || skip_picker) {
                if (Utils.variantIsOnStock(variants[0])) {
                    addToCart(variants[0]);
                } else {
                    setSodOut(true);
                }
                setLoading(false);
            } else {
                if (!dataFetched && !isPreview) {
                    const data = await getDefaultSubscriptionsData(
                        `/products/${product_info.handle}`
                    );
                    setPlansData(
                        window.mu_filter_fbt_subscription
                            ? window.mu_filter_fbt_subscription(data)
                            : data
                    );
                }
                setVariantPickerOptions({
                    product,
                    buttonText: add_to_unlock.upsell_confirm_button.text,
                    onConfirm: (variant, sellingPlanId) => {
                        addToCart(variant, sellingPlanId);
                    },
                    onClose: async () => {
                        setLoading(false);
                    },
                    plansData,
                    priceText: add_to_unlock.product_price.text
                });
                setVariantsPickerShow(true);
            }
        } catch (error) {
            setLoading(false);
        }
    }, [product, preview_product]);

    useEffect(() => {
        if (product?.variants?.length && !!count_compare_at) {
            setCompareAtMap(
                product.variants?.reduce((acc, variant) => {
                    if (variant.compareAtPrice) {
                        acc[variant.id] = Number(variant.compareAtPrice) * 100;
                    }
                    return acc;
                }, {})
            );
        }
    }, [product]);

    const addToCart = async (variant, sellingPlanId) => {
        if (window.mu_fbt_custom_atc) {
            const response = await window.mu_fbt_custom_atc(product, variant, sellingPlanId);
            if (response) {
                setLoading(false);
                setVariantsPickerShow(false);
                return;
            }
        }
        const variant_id = Number.parseInt(
            variant.id.toString().replace('gid://shopify/ProductVariant/', '')
        );
        if (productNotAddedToCart(product)) {
            add_to_cart(
                {
                    variant_id,
                    quantity: 1,
                    selling_plan: sellingPlanId,
                    product_handle: product.handle //required for preview only
                },
                (item) => {
                    addFBTUpsell(item.id);
                    setVariantsPickerShow(false);
                    setLoading(false);
                }
            );
        } else {
            update_cart_item(
                { variant_id, selling_plan: sellingPlanId, quantity: '+1' },
                (item) => {
                    addFBTUpsell(item.id);
                    setVariantsPickerShow(false);
                    setLoading(false);
                }
            );
        }
    };

    const formatText = (text) =>
        text
            .replace(/{{product_title}}/g, product.title)
            .replace(/{{product_price}}/g, price)
            .replace(
                /{{original_price}}/g,
                original && original !== price
                    ? `<del class='mu-fbt-original-price mu-opacity-75 mu-ml-1 mu-mr-1 notranslate'>${original}</del> `
                    : ''
            );

    useEffect(() => {
        const fetchProductInfo = async () => {
            const product = products?.find(({ id }) => id == product_info.id);
            // const updatedVariants = updateVariants(product.variants);
            if (product) {
                const newOriginal = calculateOriginal(product.variants);
                updateProduct(
                    product,
                    product.variants.map((variant) => ({
                        ...product_info.variants.find(({ id }) => id == variant.id),
                        ...variant,
                        presentmentPrices: null
                    })),
                    newOriginal
                );
            } else {
                setProduct(product_info);
            }
            setLoading(false);
            if (!isPreview) {
                const data = await getDefaultSubscriptionsData(`/products/${product_info.handle}`);
                setPlansData(
                    window.mu_filter_fbt_subscription
                        ? window.mu_filter_fbt_subscription(data)
                        : data
                );
                setDataFetched(true);
            }
        };

        const updateVariants = (newVariants) => {
            return product_info.variants.map((variant) => {
                const newVariantInfo = newVariants.find(
                    (newVariant) => newVariant.id === variant.id
                );
                if (newVariantInfo) {
                    return {
                        ...newVariantInfo,
                        ...variant,
                        ...(window.mu_stop_presentment ? { presentmentPrices: null } : {}),
                        price: Number(newVariantInfo.price)
                    };
                }
                return variant;
            });
        };

        const calculateOriginal = (updatedVariants) => {
            if (!updatedVariants) return 0;
            const selected_variant = window.mu_fbt_set_default_variant
                ? updatedVariants.find(({ title, option1, option2, option3 }) => {
                      const selectedOptions = window.mu_fbt_set_default_variant(product);
                      const selectedValues = Object.values(selectedOptions);
                      const variantOptions = [option1, option2, option3];

                      return (
                          title === Object.values(selectedOptions).join(' / ') ||
                          selectedValues.every((value, index) => value === variantOptions[index])
                      );
                  })
                : updatedVariants[0];
            return (
                (updatedVariants &&
                    selected_variant?.compare_at_price &&
                    !!Number(selected_variant?.compare_at_price) &&
                    Utils.formatMoney(
                        Number(selected_variant?.compare_at_price) * 100,
                        money_format
                    )) ||
                0
            );
        };

        const updateProduct = (newProduct, updatedVariants, newOriginal) => {
            const updatedProduct = {
                ...newProduct,
                ...(window.mu_fbt_use_fetched_product
                    ? {
                          fetched: true,
                          handle: newProduct.handle,
                          image: newProduct.images?.[0]?.src
                      }
                    : product_info),
                options: newProduct.options,
                variants: updatedVariants
            };
            setOriginal(newOriginal);
            setProduct(updatedProduct);
        };

        if (product_info) {
            fetchProductInfo();
        }
    }, [product_info]);

    const formattedText = useMemo(
        () => formatText(add_to_unlock.product_title.text),
        [product.title]
    );
    const formattedPrice = useMemo(
        () => formatText(add_to_unlock.product_price.text),
        [price, original]
    );

    return (
        <div
            className={classNames('fbt-item border-b last:border-b-0 mx-2 py-2')}
            style={{
                borderColor: Utils.formatColor(add_to_unlock.borderColor)
            }}
            data-loading={`${loading}`}
        >
            <div className={classNames('flex')}>
                {product.image || product.variants[0].image ? (
                    <div
                        className={classNames('fbt-item-image rounded bg-cover bg-center block')}
                        style={{
                            backgroundImage: `url(${(
								window.mu_skipImageResize
									? (product.image || product.images?.[0]?.src)
									: (product.image || product.images?.[0]?.src)?.replace(/\.jpg|\.png|\.webp/, '_small$&')	
							)})`,
                            width: 70,
                            height: 70
                        }}
                    />
                ) : (
                    <div
                        style={{
                            width: 70,
                            height: 70,
                            fill: Utils.formatColor(secondary),
                            borderColor: Utils.formatColor(secondary)
                        }}
                        className={classNames(
                            'fbt-item-image text-transparent flex-center rounded border'
                        )}
                    >
                        {product_info.fetch ? (
                            <svg viewBox="0 0 20 20" className={classNames('w-1/2 h-1/2')}>
                                <path fill="currentColor" d="M14 9l-5 5-3-2-5 3v4h18v-6z"></path>
                                <path d="M19 0H1a1 1 0 0 0-1 1v18a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1V1a1 1 0 0 0-1-1zM8 6c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm0 4c1.654 0 3-1.346 3-3S9.654 4 8 4 5 5.346 5 7s1.346 3 3 3zm-6 8v-2.434l3.972-2.383 2.473 1.65c.398.264.925.21 1.262-.126l4.367-4.367L18 13.48V18H2zM18 2v8.92l-3.375-2.7a.999.999 0 0 0-1.332.073l-4.42 4.42-2.318-1.545a1 1 0 0 0-1.07-.025L2 13.233V2h16z"></path>
                            </svg>
                        ) : (
                            <svg
                                aria-hidden="true"
                                focusable="false"
                                data-icon="spinner-third"
                                role="img"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                                className={classNames('w-4 h-4 fa-spin')}
                                style={{ color: Utils.formatColor(secondary) }}
                            >
                                <path
                                    fill="currentColor"
                                    d="M456.433 371.72l-27.79-16.045c-7.192-4.152-10.052-13.136-6.487-20.636 25.82-54.328 23.566-118.602-6.768-171.03-30.265-52.529-84.802-86.621-144.76-91.424C262.35 71.922 256 64.953 256 56.649V24.56c0-9.31 7.916-16.609 17.204-15.96 81.795 5.717 156.412 51.902 197.611 123.408 41.301 71.385 43.99 159.096 8.042 232.792-4.082 8.369-14.361 11.575-22.424 6.92z"
                                ></path>
                            </svg>
                        )}
                    </div>
                )}
                <div className={classNames('flex-1 flex flex-col', rtl ? 'pr-2' : 'pl-2')}>
                    <b
                        className={classNames(
                            'fbt-item-title mb-0 overflow-hidden w-full line-clamp'
                        )}
                        style={{
                            fontSize: Utils.em(add_to_unlock.product_title.font_size),
                            fontWeight: add_to_unlock.product_title.font_weight,
                            color: Utils.formatColor(add_to_unlock.product_title.color)
                        }}
                    >
                        {isPreview || window.mu_fbt_title_unclickable ? (
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: formattedText
                                }}
                            />
                        ) : (
                            <a
                                href={`${window.origin}/products/${product.handle}`}
                                dangerouslySetInnerHTML={{
                                    __html: formattedText
                                }}
                            />
                        )}
                    </b>
                    <div className={classNames('flex items-center flex-wrap')}>
                        <div
                            className={classNames(
                                'flex flex-col flex-1 items-start',
                                rtl ? 'ml-2' : 'mr-2'
                            )}
                        >
                            <b
                                className={`${classNames('fbt-item-price mb-0')} notranslate`}
                                style={{
                                    fontSize: Utils.em(add_to_unlock.product_price.font_size),
                                    fontWeight: add_to_unlock.product_price.font_weight,
                                    color: Utils.formatColor(add_to_unlock.product_price.color)
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: formattedPrice
                                }}
                                data-display={formattedPrice}
                            />

                            {window.mu_fbt_replace_show_details?.find(
                                (elm) => elm.id === product.id
                            ) ? (
                                <p>
                                    {
                                        window.mu_fbt_replace_show_details?.find(
                                            (elm) => elm.id === product.id
                                        )?.text
                                    }
                                </p>
                            ) : (
                                <button
                                    className={classNames('fbt-item-link btn-link')}
                                    style={{
                                        color: Utils.formatColor(
                                            add_to_unlock.show_product_details.color
                                        ),
                                        fontWeight: add_to_unlock.show_product_details.font_weight,
                                        fontSize: Utils.em(
                                            add_to_unlock.show_product_details.font_size
                                        )
                                    }}
                                    onClick={() => {
                                        if (!product.isExample) preview_product(product);
                                    }}
                                >
                                    {add_to_unlock.show_product_details.text}
                                </button>
                            )}
                        </div>

                        <button
                            className={classNames(
                                'fbt-item-atc border-solid border-2 bg-transparent btn-small py-2 relative'
                            )}
                            style={{
                                borderColor: Utils.formatColor(
                                    add_to_unlock.upsell_add_button.color || primary
                                ),
                                color: Utils.formatColor(
                                    add_to_unlock.upsell_add_button.color || primary
                                ),
                                fontWeight: add_to_unlock.upsell_add_button.font_weight,
                                fontSize: Utils.em(add_to_unlock.upsell_add_button.font_size)
                            }}
                            onClick={() => addToCartInit()}
                            disabled={sodOut || !product.fetched || loading}
                        >
                            <span
                                className={classNames({
                                    'opacity-0': loading
                                })}
                            >
                                {sodOut ? sold_out : add_to_unlock.upsell_add_button.text}
                            </span>
                            <div
                                className={classNames('absolute inset-0 flex-center', {
                                    hidden: !loading
                                })}
                            >
                                <svg
                                    aria-hidden="true"
                                    focusable="false"
                                    data-icon="spinner-third"
                                    role="img"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 512 512"
                                    className={classNames('w-3 h-3 fa-spin')}
                                >
                                    <path
                                        fill="currentColor"
                                        d="M456.433 371.72l-27.79-16.045c-7.192-4.152-10.052-13.136-6.487-20.636 25.82-54.328 23.566-118.602-6.768-171.03-30.265-52.529-84.802-86.621-144.76-91.424C262.35 71.922 256 64.953 256 56.649V24.56c0-9.31 7.916-16.609 17.204-15.96 81.795 5.717 156.412 51.902 197.611 123.408 41.301 71.385 43.99 159.096 8.042 232.792-4.082 8.369-14.361 11.575-22.424 6.92z"
                                    ></path>
                                </svg>
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
});

Product.propTypes = {};

export default Product;
